import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CalendarDays, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import StockModal from '@/components/StockModal';
import { fetchStockWithFallback } from '@/lib/stockFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// Round 84k Phase 5.7 (Feb 15, 2026) — paginate at 25 events per page,
// matching the Dashboard stock-list pattern. Picked deliberately because
// monthly ex-div sweeps can return up to 200 events and users were
// staring down an endless scroll.
const PAGE_SIZE = 25;

/**
 * Community Dividend Calendar — Phase 1 Step 9.
 *
 * Renders the month's ex-dividend events ranked chronologically and
 * paginated 25-per-page. Each event shows ticker + ex-date + pay-date
 * + amount, plus a community sentiment overlay (% buying/holding) if a
 * recent poll exists. The ticker chip is clickable and opens the main
 * Stock Modal at the Dashboard route via `reopenModalTicker` (same
 * pattern as `BackOrHomeButton` uses).
 */
export default function CommunityCalendarTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  // Round 84k Phase 5.8 (Feb 15, 2026) — open the Stock Modal IN-PLACE
  // on the Calendar tab instead of navigating to the Dashboard. The
  // previous `reopenModalTicker` route bounced the user away from the
  // Calendar; this version keeps them right here.
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStock, setModalStock] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/community/calendar`, {
          params: { year, month },
          withCredentials: true,
        });
        if (cancelled) return;
        setEvents(data.events || []);
        setPage(1);  // any month change resets to first page
      } catch {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [year, month]);

  const prevMonth = () => {
    let m = month - 1, y = year;
    if (m < 1) { m = 12; y -= 1; }
    setYear(y); setMonth(m);
  };
  const nextMonth = () => {
    let m = month + 1, y = year;
    if (m > 12) { m = 1; y += 1; }
    setYear(y); setMonth(m);
  };

  // Sort chronologically once, then slice the page.
  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) =>
      String(a.ex_dividend_date || '').localeCompare(String(b.ex_dividend_date || ''))
    );
  }, [events]);
  const totalPages = Math.max(1, Math.ceil(sortedEvents.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageEvents = sortedEvents.slice(pageStart, pageStart + PAGE_SIZE);

  // Group ONLY the page slice by ex-date — keeps each page's
  // date-header rendering simple and matches the user's original layout.
  const groupedPage = useMemo(() => {
    const g = {};
    for (const e of pageEvents) {
      const d = e.ex_dividend_date || 'unknown';
      if (!g[d]) g[d] = [];
      g[d].push(e);
    }
    return g;
  }, [pageEvents]);
  const sortedDates = Object.keys(groupedPage).sort();

  const handleTickerClick = async (ticker) => {
    if (!ticker) return;
    setModalOpen(true);
    setModalLoading(true);
    setModalStock(null);
    try {
      const data = await fetchStockWithFallback(ticker, {
        timeoutMs: 3500,
        onFresh: (fresh) => setModalStock(fresh),
      });
      setModalStock(data);
    } catch {
      setModalStock(null);
    } finally {
      setModalLoading(false);
    }
  };

  // Round 84k Phase 5.8b (Feb 15, 2026) — DO NOT use `new Date(iso)`
  // on bare YYYY-MM-DD strings: that constructor parses them as UTC
  // midnight, which renders as the PREVIOUS day in every US time
  // zone (UTC-5 through UTC-8). User reported every ex-div date was
  // showing one day early ("April 30" instead of "May 1"). Parsing
  // manually keeps the date in local time.
  const parseLocalDate = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim();
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (m) {
      return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    }
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const formatPayDate = (raw) => {
    const d = parseLocalDate(raw);
    if (!d) return raw || null;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  return (
    <div className="space-y-4" data-testid="community-calendar-tab">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <CalendarDays className="w-5 h-5 text-violet-500 dark:text-violet-400" />
          <h2 className="text-base sm:text-lg font-bold">{MONTHS[month - 1]} {year}</h2>
          {!loading && events.length > 0 && (
            <span
              className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300"
              data-testid="community-calendar-count"
            >
              {events.length} {events.length === 1 ? 'stock' : 'stocks'}
            </span>
          )}
        </div>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" onClick={prevMonth} data-testid="community-calendar-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={nextMonth} data-testid="community-calendar-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading…</div>
      )}

      {!loading && events.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-muted/20 p-6 text-center text-sm text-muted-foreground" data-testid="community-calendar-empty">
          No ex-dividend dates this month for any ticker in our database. Try the {' '}
          <button onClick={prevMonth} className="underline hover:text-foreground">previous</button> or {' '}
          <button onClick={nextMonth} className="underline hover:text-foreground">next</button> month.
        </div>
      )}

      {!loading && events.length > 0 && (
        <div className="space-y-3">
          {sortedDates.map((d) => (
            <div key={d} className="rounded-lg border border-border bg-card overflow-hidden">
              <div className="px-3 py-2 bg-violet-50 dark:bg-violet-950/30 border-b border-border">
                <div className="text-xs font-semibold text-violet-700 dark:text-violet-300 uppercase tracking-wider">
                  Ex-Div {parseLocalDate(d)?.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) || d}
                </div>
              </div>
              <ul className="divide-y divide-border">
                {groupedPage[d].map((e) => {
                  const payLabel = formatPayDate(e.payment_date);
                  return (
                    <li key={e.ticker} className="px-3 py-2 flex items-center gap-3" data-testid={`community-calendar-row-${e.ticker}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <button
                            type="button"
                            onClick={() => handleTickerClick(e.ticker)}
                            className="font-bold text-violet-700 dark:text-violet-300 hover:text-violet-900 dark:hover:text-violet-100 underline-offset-2 hover:underline transition-colors"
                            data-testid={`community-calendar-ticker-${e.ticker}`}
                            title={`Open ${e.ticker} stock modal`}
                          >
                            {e.ticker}
                          </button>
                          <span className="text-xs text-muted-foreground truncate">{e.name}</span>
                          {e.members_holding > 0 && (
                            <span className="text-[10px] inline-flex items-center gap-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 border border-emerald-200/60 dark:border-emerald-700/50" data-testid={`calendar-holders-${e.ticker}`}>
                              🔥 {e.members_holding.toLocaleString()} hold
                            </span>
                          )}
                          {e.discussion_count > 0 && (
                            <span className="text-[10px] inline-flex items-center gap-0.5 rounded-full bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300 px-1.5 py-0.5 border border-violet-200/60 dark:border-violet-700/50" data-testid={`calendar-discussions-${e.ticker}`}>
                              💬 {e.discussion_count}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                          <span>${e.dividend_rate ? Number(e.dividend_rate).toFixed(4) : '—'}/share</span>
                          <span className="text-emerald-700 dark:text-emerald-400">
                            Ex-Div: {parseLocalDate(d)?.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) || d}
                          </span>
                          {payLabel ? (
                            <span className="text-cyan-700 dark:text-cyan-400">Pay: {payLabel}</span>
                          ) : (
                            <span className="text-muted-foreground/70">Pay: TBD</span>
                          )}
                        </div>
                      </div>
                      {e.community_sentiment_buying_pct != null && (
                        <div className="text-right shrink-0">
                          <div className="text-[10px] text-muted-foreground">Sentiment</div>
                          <div className={`text-xs font-bold ${
                            e.community_sentiment_buying_pct >= 70 ? 'text-emerald-600 dark:text-emerald-400'
                            : e.community_sentiment_buying_pct >= 40 ? 'text-amber-600 dark:text-amber-400'
                            : 'text-rose-600 dark:text-rose-400'
                          }`}>
                            {e.community_sentiment_buying_pct}% buying/holding
                          </div>
                          <div className="text-[10px] text-muted-foreground">{e.community_sentiment_total} votes</div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2" data-testid="community-calendar-pagination">
              <Button
                variant="outline"
                size="sm"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="community-calendar-prev-page"
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground px-3">
                Page {safePage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={safePage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                data-testid="community-calendar-next-page"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}

      <StockModal
        stock={modalStock}
        open={modalOpen}
        onClose={() => { setModalOpen(false); setModalStock(null); }}
        loading={modalLoading}
      />
    </div>
  );
}
